import { Link } from 'react-router-dom';
import { PremiumCard, SectionHeader } from '../premium';
import { NavIcon } from '../NavIcons';
import type { CircleDashboard } from '../../types/circles';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function CircleDashboardCard({ data }: { data: CircleDashboard }) {
  if (!data.enabled) return null;

  const primary = data.circles[0];

  return (
    <div>
      <SectionHeader
        title="Circles"
        action={
          <Link to="/circles" className="text-xs font-semibold text-violet-300">
            View all →
          </Link>
        }
      />
      <PremiumCard variant="ai" className="!p-0 !overflow-hidden">
        {primary ? (
          <Link to={`/circles/${primary.id}`} className="block border-b border-white/8 p-4 hover:bg-white/[0.02]">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <NavIcon name="circles" className="h-4 w-4 text-violet-300" />
                  <span className="truncate font-display text-base font-bold text-white">{primary.name}</span>
                </div>
                <p className="mt-1 text-xs text-zinc-400">
                  {primary.memberCount} members · {primary.weeklyProgressPct}% momentum today
                </p>
              </div>
              <div className="shrink-0 rounded-2xl bg-violet-500/20 px-3 py-2 text-center ring-1 ring-violet-500/30">
                <div className="text-lg font-bold tabular-nums text-white">{primary.yourDailyScore}</div>
                <div className="text-[9px] uppercase text-violet-200/80">You</div>
              </div>
            </div>
          </Link>
        ) : (
          <div className="border-b border-white/8 p-4">
            <p className="text-sm text-zinc-300">Join a Circle for accountability with friends.</p>
            <Link to="/circles" className="mt-2 inline-block text-xs font-semibold text-violet-300">
              Create or join →
            </Link>
          </div>
        )}
        {data.activities.length > 0 ? (
          <ul className="divide-y divide-white/6">
            {data.activities.slice(0, 4).map((a) => (
              <li key={a.id}>
                <Link
                  to={`/circles/${a.circleId}`}
                  className="flex gap-3 px-4 py-3 hover:bg-white/[0.02]"
                >
                  <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-gradient-to-br from-sky-400 to-violet-400" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm leading-snug text-zinc-200">{a.message}</p>
                    <p className="mt-0.5 text-[11px] text-zinc-500">
                      {a.circleName} · {timeAgo(a.createdAt)}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-3 text-xs text-zinc-500">Circle activity from friends will show up here.</p>
        )}
      </PremiumCard>
    </div>
  );
}
